// src/account.ts
// "Download my data" and "Delete my account".
//
// Everything here is something the rules let the owner read or delete.
// Deleting removes: your profile, private notes, block list, your posts
// (named and anonymous), community room threads, replies, and hearts,
// letters you wrote, pen pal requests and kudos you sent or received,
// and your pen pal links. Letters other people
// wrote to you are theirs, so they aren't touched (you just can't see
// them anymore). Reports stay for safety.
import {
  DocumentReference,
  QueryDocumentSnapshot,
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { EmailAuthProvider, User, deleteUser, reauthenticateWithCredential } from "firebase/auth";
import { db } from "./firebase";
import { Circle, leaveCircle } from "./circles";
import { PACE } from "./pace";

type Found = { path: string; ref: DocumentReference; data: Record<string, unknown> };

const fromSnap = (d: QueryDocumentSnapshot): Found => ({ path: d.ref.path, ref: d.ref, data: d.data() });

async function find(col: string, field: string, value: string, op: "==" | "array-contains" = "==") {
  const snap = await getDocs(query(collection(db, col), where(field, op, value)));
  return snap.docs.map(fromSnap);
}

// Everything that belongs to this account, grouped for the download.
async function gather(uid: string) {
  const profile = await getDoc(doc(db, "users", uid));
  const privateDocs = (await getDocs(collection(db, "users", uid, "private"))).docs.map(fromSnap);
  const blocks = (await getDocs(collection(db, "users", uid, "blocks"))).docs.map(fromSnap);

  const anon = privateDocs.find((d) => d.ref.id === "anon");
  const anonAdIds = Array.isArray(anon?.data.adIds) ? (anon!.data.adIds as string[]) : [];

  const namedAds = await find("penpalAds", "uid", uid);
  const anonAds: Found[] = [];
  for (const id of anonAdIds) {
    const snap = await getDoc(doc(db, "penpalAds", id));
    if (snap.exists()) anonAds.push({ path: snap.ref.path, ref: snap.ref, data: snap.data() });
  }

  const toMyAds = async (col: string) =>
    (await Promise.all(anonAdIds.map((id) => find(col, "toAd", id)))).flat();

  // Community room posts, replies, and hearts are listed in private/rooms
  // (so anonymous ones can be found too). Keys are threadId or threadId_replyId.
  const rooms = privateDocs.find((d) => d.ref.id === "rooms")?.data || {};
  const list = (k: string) => (Array.isArray(rooms[k]) ? (rooms[k] as string[]) : []);
  const fetchDoc = async (path: string[]): Promise<Found | null> => {
    const ref = doc(db, path[0], ...path.slice(1));
    const snap = await getDoc(ref).catch(() => null);
    return snap?.exists() ? { path: ref.path, ref, data: snap.data() } : null;
  };
  const replyPath = (key: string) => {
    const [t, r] = key.split("_");
    return ["roomThreads", t, "replies", r];
  };
  const found = (xs: (Found | null)[]) => xs.filter((x): x is Found => !!x);
  const roomThreads = found(await Promise.all(list("threads").map((t) => fetchDoc(["roomThreads", t]))));
  const roomReplies = found(await Promise.all(list("replies").map((k) => fetchDoc(replyPath(k)))));
  const roomHearts = found(
    await Promise.all(
      list("hearts").map((k) =>
        fetchDoc(k.includes("_") ? [...replyPath(k), "hearts", uid] : ["roomThreads", k, "hearts", uid])
      )
    )
  );
  // Private circles: posts ("cid_pid"), comments ("cid_pid_rid"), the circles
  // you're in, and circles you asked to join.
  const circlePosts = found(
    await Promise.all(
      list("circlePosts").map((k) => {
        const [c, p] = k.split("_");
        return fetchDoc(["circles", c, "posts", p]);
      })
    )
  );
  const circleReplies = found(
    await Promise.all(
      list("circleReplies").map((k) => {
        const [c, p, r] = k.split("_");
        return fetchDoc(["circles", c, "posts", p, "replies", r]);
      })
    )
  );
  const circles = found(await Promise.all(list("circles").map((c) => fetchDoc(["circles", c]))));
  const circleMemberships = found(
    await Promise.all(list("circles").map((c) => fetchDoc(["circles", c, "members", uid])))
  );
  const circleJoinRequests = found(
    await Promise.all(list("circleRequests").map((c) => fetchDoc(["circles", c, "joinRequests", uid])))
  );

  // The PenPal Post: your pieces ("pid") and comments ("pid_rid").
  const postPieces = found(await Promise.all(list("postPieces").map((p) => fetchDoc(["postPieces", p]))));
  const postReplies = found(
    await Promise.all(
      list("postReplies").map((k) => {
        const [p, r] = k.split("_");
        return fetchDoc(["postPieces", p, "replies", r]);
      })
    )
  );

  // when you last posted each kind of thing (for the speed limits)
  const pace = found(await Promise.all(Object.keys(PACE).map((k) => fetchDoc(["pace", uid, "kinds", k]))));

  // the hidden author records of your anonymous posts (only yours are readable)
  const roomAuthors = found(
    await Promise.all(
      [...list("threads"), ...list("replies"), ...list("circlePosts"), ...list("circleReplies"),
       ...[...list("postPieces"), ...list("postReplies")].map((k) => `pp_${k}`)].map((k) =>
        fetchDoc(["roomAuthors", k])
      )
    )
  );

  return {
    profile: profile.exists()
      ? [{ path: profile.ref.path, ref: profile.ref, data: profile.data() }]
      : [],
    privateDocs,
    blocks,
    ads: [...namedAds, ...anonAds],
    lettersSent: await find("messages", "senderId", uid),
    lettersReceived: await find("messages", "receiverId", uid),
    requests: [
      ...(await find("penpalRequests", "from", uid)),
      ...(await find("penpalRequests", "to", uid)),
      ...(await toMyAds("penpalRequests")),
    ],
    kudos: [
      ...(await find("kudos", "from", uid)),
      ...(await find("kudos", "to", uid)),
      ...(await toMyAds("kudos")),
    ],
    penpalLinks: await find("penpals", "members", uid, "array-contains"),
    roomThreads,
    roomReplies,
    roomHearts,
    roomAuthors,
    circles,
    circleMemberships,
    circleJoinRequests,
    circlePosts,
    circleReplies,
    postPieces,
    postReplies,
    pace,
  };
}

// Firestore timestamps become readable dates in the download.
function plain(value: unknown): unknown {
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (Array.isArray(value)) return value.map(plain);
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, plain(v)]));
  }
  return value;
}

export async function downloadMyData(user: User) {
  const groups = await gather(user.uid);
  const out: Record<string, unknown> = {
    exportedAt: new Date().toISOString(),
    account: { id: user.uid, email: user.email, emailVerified: user.emailVerified },
  };
  for (const [name, docs] of Object.entries(groups)) {
    // de-duplicate (a request can match both "from" and "to" lists)
    const seen = new Map(docs.map((d) => [d.path, { id: d.ref.id, ...(plain(d.data) as object) }]));
    out[name] = Array.from(seen.values());
  }
  const blob = new Blob([JSON.stringify(out, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `penpal-board-my-data-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Deleting needs a fresh sign-in, so we ask for their password first.
export async function deleteMyAccount(user: User, password: string) {
  if (!user.email) throw new Error("no-email");
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, password));

  const g = await gather(user.uid);

  // Circles you lead go to a first mate (or pause) before you leave them.
  for (const c of g.circles) {
    const circle = { id: c.ref.id, ...(c.data as Omit<Circle, "id">) } as Circle;
    if (circle.quartermaster === user.uid) {
      await leaveCircle(circle, user.uid).catch((err) => console.warn("Couldn't hand off circle:", err));
    }
  }

  // Order matters: anonymous posts are deleted while the private list
  // that proves you own them still exists; the profile goes last.
  const steps: Found[][] = [
    g.ads,
    g.requests,
    g.kudos,
    g.lettersSent,
    // room and circle posts go before the hidden author records that prove they're yours
    g.circleReplies,
    g.circlePosts,
    g.postReplies,
    g.postPieces,
    g.circleJoinRequests,
    g.circleMemberships,
    g.roomReplies,
    g.roomThreads,
    g.roomHearts,
    g.roomAuthors,
    g.penpalLinks,
    g.blocks,
    g.privateDocs,
    g.profile,
    g.pace, // only deletable once the profile is gone
  ];
  for (const docs of steps) {
    const unique = Array.from(new Map(docs.map((d) => [d.path, d.ref])).values());
    for (let i = 0; i < unique.length; i += 400) {
      const batch = writeBatch(db);
      unique.slice(i, i + 400).forEach((ref) => batch.delete(ref));
      await batch.commit();
    }
  }
  await deleteUser(user);
}
